/**
 * One-off Ad License bought for a single approved video on a UGC-only brief.
 * Mirrors the API contract in `brief-ad-licenses` — all money is integer minor units (pence/cents).
 */

export enum AdLicenseStatusEnum {
  Pending = 1,
  Accepted = 2,
  Declined = 3,
  /** The creator did not respond inside the response window. */
  Expired = 4,
  Cancelled = 5,
}

export type AdLicenseDisplayStatus =
  | 'pending'
  | 'licensed'
  /** Accepted, but the licence term has ended. */
  | 'expired'
  | 'declined'
  | 'request_expired'
  | 'cancelled';

export type AdLicenseDeclineReason = 'price' | 'no_ads' | 'rights' | 'other';

export interface AdLicenseRightDto {
  key: string;
  title: string;
  description: string;
}

export interface AdLicenseDto {
  id: string;
  status: AdLicenseStatusEnum;
  displayStatus: AdLicenseDisplayStatus;
  briefId: string;
  briefApplicationId: string;
  videoId: string;
  creatorId: string;
  brandName: string;
  draftLabel: string;
  posterUrl: string | null;
  durationSeconds: number | null;
  currencyCode: string;
  brandPrice: number;
  brandPriceNet: number;
  vatAmount: number;
  creatorEarnings: number;
  feePct: number;
  termDays: number;
  rights: AdLicenseRightDto[];
  requestedAt: string;
  respondBy: string;
  respondedAt: string | null;
  acceptedAt: string | null;
  expiresAt: string | null;
  declineReason: AdLicenseDeclineReason | null;
}

export type AdLicenseIneligibleReason =
  | 'not_ugc_only'
  | 'already_licensed_brief'
  | 'no_pricing_model'
  | 'no_price_difference'
  | 'currency_mismatch'
  | 'application_not_hired';

export interface AdLicenseQuoteDto {
  eligible: boolean;
  reason?: AdLicenseIneligibleReason;
  currencyCode: string;
  brandPrice: number;
  brandPriceNet: number;
  vatAmount: number;
  creatorEarnings: number;
  feePct: number;
  termDays: number;
  responseDays: number;
  rights: AdLicenseRightDto[];
}

export interface AdLicenseApplicationStateDto {
  quote: AdLicenseQuoteDto;
  licenses: AdLicenseDto[];
}

export interface AdLicenseWalletBalanceDto {
  currentBalance: number;
  availableBalance: number;
  businessAccountId: string;
}

export interface AdLicenseMutationResponseDto {
  license: AdLicenseDto;
  walletBalance: AdLicenseWalletBalanceDto;
}

export interface CreateAdLicenseRequestDto {
  videoId: string;
  termsAccepted: true;
}

/**
 * The licence slot a video card shows. Worked out on the client from the video status,
 * the quote and the latest licence for that video.
 */
export type AdLicenseSlotState =
  | 'hidden'
  | 'locked'
  | 'canBuy'
  | 'pending'
  | 'licensed'
  | 'expired';

export const AD_LICENSE_DECLINE_REASON_LABELS: Record<AdLicenseDeclineReason, string> = {
  price: 'Price too low',
  no_ads: "Doesn't want to appear in ads",
  rights: 'Music or content rights',
  other: 'Other',
};

// TODO(ad-license): replace with the final, legal-approved Ad License terms URL.
export const AD_LICENSE_TERMS_URL = 'https://www.shout.com/ad-license-terms';
